import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { PERMISSIONS_PAGE_SIZE, usePermissionPassbooks } from '@/features/permissions/api/use-permissions';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Skeleton } from '@/shared/ui/skeleton';
import type { TemplateStoreRef } from '../model/types';

export type ApplyMode = 'all' | 'selected';

/**
 * Plataforma: para quem o template vale. "Todas" é o padrão do e-mail do
 * sistema; "selecionadas" salva uma variante só para essas lojas e deixa o
 * padrão das outras como está.
 */
export function ApplyToField({
  mode,
  onMode,
  stores,
  onStores,
  lockedToSelected = false,
  className,
}: {
  mode: ApplyMode;
  onMode: (mode: ApplyMode) => void;
  stores: TemplateStoreRef[];
  onStores: (stores: TemplateStoreRef[]) => void;
  /** Variante existente: só muda a lista de lojas. */
  lockedToSelected?: boolean;
  className?: string;
}) {
  const selecting = lockedToSelected || mode === 'selected';
  return (
    <Card className={cn('space-y-4 overflow-visible p-4 sm:p-5', className)}>
      <div>
        <p className="text-sm font-medium">Aplicar em</p>
        {lockedToSelected ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Variante: vale só para as lojas abaixo. O padrão das outras lojas não muda.
          </p>
        ) : (
          <RadioGroup className="mt-2" value={mode} onValueChange={(value) => onMode(value as ApplyMode)}>
            <label htmlFor="aplicar-todas" className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="all" id="aplicar-todas" /> Todas as lojas (padrão)
            </label>
            <label htmlFor="aplicar-algumas" className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="selected" id="aplicar-algumas" /> Só as lojas selecionadas
            </label>
          </RadioGroup>
        )}
        {!lockedToSelected && mode === 'selected' ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Salva uma variante só para essas lojas; o padrão continua valendo para as demais.
          </p>
        ) : null}
      </div>
      {selecting ? <StorePicker stores={stores} onStores={onStores} /> : null}
    </Card>
  );
}

function StorePicker({ stores, onStores }: { stores: TemplateStoreRef[]; onStores: (stores: TemplateStoreRef[]) => void }) {
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, isLoading, isFetching } = usePermissionPassbooks(term, page);
  const rows = data?.rows ?? [];
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / PERMISSIONS_PAGE_SIZE));
  const selected = new Set(stores.map((store) => store.id));
  const toggle = (row: { id: string; name: string }) =>
    onStores(selected.has(row.id) ? stores.filter((store) => store.id !== row.id) : [...stores, { id: row.id, name: row.name }]);

  return (
    <div className="space-y-3">
      {stores.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {stores.map((store) => (
            <Button
              key={store.id}
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onStores(stores.filter((item) => item.id !== store.id))}
              aria-label={`Tirar ${store.name}`}
            >
              {store.name} <X className="size-3" />
            </Button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Nenhuma loja selecionada.</p>
      )}
      <div>
        <Label htmlFor="busca-loja-template" className="sr-only">
          Buscar loja
        </Label>
        <Input
          id="busca-loja-template"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar loja ou cliente"
          autoComplete="off"
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-xl border border-border/60">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-5" />
            <Skeleton className="h-5" />
            <Skeleton className="h-5" />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">Nenhuma loja encontrada.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {rows.map((row) => (
              <li key={row.id}>
                <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-accent/50">
                  <Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggle(row)} aria-label={row.name} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{row.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {row.clientName}
                      {row.inactive ? ' · inativa' : ''}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {pages > 1 ? (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Página {page} de {pages}
            {isFetching ? ' …' : ''}
          </span>
          <div className="flex gap-1">
            <Button type="button" variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
              Anterior
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((current) => current + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
